import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

export const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer data-testid="main-footer">
      {/* Main Footer Content */}
      <div className="bg-gradient-to-b from-[#f8fafb] to-[#f0f5f7] border-t border-[#e2e8f0]">
        <div className="container-custom py-16 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Column 1: Brand */}
            <div>
              <div className="flex items-center gap-2 mb-6">
                <img 
                  src="https://customer-assets.emergentagent.com/job_98872b8f-1d95-46de-bdd8-15acab8c6ffc/artifacts/5h271v8q_0A4AD33B-1D8D-4D91-84DE-B5E3E6EA9948.png"
                  alt="Phönix Pflege Team Logo"
                  className="h-12 w-auto"
                />
              </div>
              <p className="text-[#64748b] leading-relaxed text-sm">
                Ambulante Pflege in Itzehoe mit Herz und Sicherheit. 
                Wir sind für Sie da – persönlich, kompetent und zuverlässig.
              </p>
            </div>

            {/* Column 2: Quick Links */}
            <div>
              <h4 className="text-[#1A52D8] font-semibold mb-5 text-sm uppercase tracking-wider">
                Navigation
              </h4>
              <nav className="flex flex-col gap-3" aria-label="Footer Navigation">
                <Link 
                  to="/" 
                  className="text-[#64748b] hover:text-[#1A52D8] transition-colors duration-200 text-sm"
                  data-testid="footer-link-home"
                >
                  Startseite
                </Link>
                <Link 
                  to="/leistungen" 
                  className="text-[#64748b] hover:text-[#1A52D8] transition-colors duration-200 text-sm"
                  data-testid="footer-link-services"
                >
                  Unsere Leistungen
                </Link>
                <Link 
                  to="/ueber-uns" 
                  className="text-[#64748b] hover:text-[#1A52D8] transition-colors duration-200 text-sm"
                  data-testid="footer-link-about"
                >
                  Über uns
                </Link>
                <Link 
                  to="/kontakt" 
                  className="text-[#64748b] hover:text-[#1A52D8] transition-colors duration-200 text-sm"
                  data-testid="footer-link-contact"
                >
                  Kontakt
                </Link>
              </nav>
            </div>

            {/* Column 3: Contact */}
            <div>
              <h4 className="text-[#1A52D8] font-semibold mb-5 text-sm uppercase tracking-wider">
                Kontakt
              </h4>
              <div className="flex flex-col gap-4">
                <div className="flex items-start gap-3 text-[#64748b] text-sm">
                  <div className="w-8 h-8 rounded-full bg-[#23D3D3]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <MapPin className="w-4 h-4 text-[#23D3D3]" />
                  </div>
                  <div>
                    <p className="font-medium text-[#1e293b]">Lindenbusch 13a</p>
                    <p>25578 Dägeling</p>
                  </div>
                </div>
                <a 
                  href="tel:+4915146173685" 
                  className="flex items-center gap-3 text-[#64748b] hover:text-[#1A52D8] transition-colors duration-200 text-sm group"
                  data-testid="footer-phone"
                >
                  <div className="w-8 h-8 rounded-full bg-[#1A52D8]/10 flex items-center justify-center group-hover:bg-[#1A52D8]/20 transition-colors">
                    <Phone className="w-4 h-4 text-[#1A52D8]" />
                  </div>
                  Jetzt Anrufen
                </a>
                <a 
                  href="mailto:info@pflegeteam-itzehoe.de" 
                  className="flex items-center gap-3 text-[#64748b] hover:text-[#1A52D8] transition-colors duration-200 text-sm group"
                  data-testid="footer-email"
                >
                  <div className="w-8 h-8 rounded-full bg-[#1A52D8]/10 flex items-center justify-center group-hover:bg-[#1A52D8]/20 transition-colors">
                    <Mail className="w-4 h-4 text-[#1A52D8]" />
                  </div>
                  info@pflegeteam-itzehoe.de
                </a>
                <div className="flex items-center gap-3 text-[#64748b] text-sm">
                  <div className="w-8 h-8 rounded-full bg-[#23D3D3]/10 flex items-center justify-center">
                    <Clock className="w-4 h-4 text-[#23D3D3]" />
                  </div>
                  24/7 erreichbar
                </div>
              </div>
            </div>

            {/* Column 4: Service Area - ALLE 18 Städte */}
            <div>
              <h4 className="text-[#1A52D8] font-semibold mb-5 text-sm uppercase tracking-wider">
                Einzugsgebiet (18 Orte)
              </h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                {[
                  { name: 'Itzehoe', slug: 'itzehoe' },
                  { name: 'Glückstadt', slug: 'glueckstadt' },
                  { name: 'Wilster', slug: 'wilster' },
                  { name: 'Kellinghusen', slug: 'kellinghusen' },
                  { name: 'Krempe', slug: 'krempe' },
                  { name: 'Hohenlockstedt', slug: 'hohenlockstedt' },
                  { name: 'Dägeling', slug: 'daegeling' },
                  { name: 'Heiligenstedten', slug: 'heiligenstedten' },
                  { name: 'Lägerdorf', slug: 'laegerdorf' },
                  { name: 'Breitenburg', slug: 'breitenburg' },
                  { name: 'Wewelsfleth', slug: 'wewelsfleth' },
                  { name: 'Horst', slug: 'horst-holstein' },
                  { name: 'Schenefeld', slug: 'schenefeld-steinburg' },
                  { name: 'Münsterdorf', slug: 'muensterdorf' },
                  { name: 'Brokdorf', slug: 'brokdorf' },
                  { name: 'Kollmar', slug: 'kollmar' },
                  { name: 'Ecklak', slug: 'ecklak' },
                  { name: 'Kaisborstel', slug: 'kaisborstel' }
                ].map((city) => (
                  <Link 
                    key={city.slug}
                    to={`/ambulante-pflege/${city.slug}`}
                    className="flex items-center gap-2 text-[#64748b] hover:text-[#1A52D8] transition-colors duration-200 text-sm group"
                  >
                    <MapPin className="w-3 h-3 text-[#23D3D3] group-hover:text-[#1A52D8] transition-colors flex-shrink-0" />
                    {city.name}
                  </Link>
                ))}
              </div>
              <p className="text-xs text-[#94a3b8] mt-4">
                + weitere Gemeinden im Kreis Steinburg
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Legal Notice - Minimal */}
      <div className="bg-[#f0f5f7] border-t border-[#e2e8f0]">
        <div className="container-custom py-4">
          <p className="text-center text-[#94a3b8] text-xs" data-testid="legal-notice">
            Leistungserbringer: Phönix Pflegeteam Itzehoe, Lindenbusch 13a, 25578 Dägeling
          </p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="bg-[#1e293b]">
        <div className="container-custom py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-[#94a3b8]">
            <p>© {currentYear} Phönix Pflege Team Itzehoe</p>
            <div className="flex items-center gap-6">
              <Link 
                to="/impressum" 
                className="hover:text-white transition-colors duration-200"
                data-testid="footer-impressum"
              >
                Impressum
              </Link>
              <Link 
                to="/datenschutz" 
                className="hover:text-white transition-colors duration-200"
                data-testid="footer-datenschutz"
              >
                Datenschutz
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
